import { createContext, useContext, useState, type ReactNode } from "react";
import { locales, type Locale, type Translations } from "./locales";

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translations;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  // Locale is locked to English in the UI. The zh translations remain in the
  // codebase (see locales.ts) but there is no switcher to reach them.
  const [locale, setLocaleState] = useState<Locale>("en");

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
  };

  const value: I18nContextValue = {
    locale,
    setLocale,
    t: locales[locale] as Translations,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
}

// Language Switcher Component
//
// The UI is locked to English. This component is intentionally a no-op so that
// the language-switcher button no longer appears anywhere, while the zh
// translations stay in the codebase (locales.ts) for future use. Callers across
// the app still import and render <LanguageSwitcher />; it simply renders nothing.
export function LanguageSwitcher() {
  return null;
}
